import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  schemaData?: Record<string, unknown>[];
}

export default function SEO({ title, description, keywords, canonicalUrl, ogType = 'website', schemaData }: SEOProps) {
  useEffect(() => {
    const currentTitle = document.title;
    document.title = title;

    const updateMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    updateMeta('description', description);
    if (keywords) updateMeta('keywords', keywords);
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    updateMeta('og:type', ogType, true);
    if (canonicalUrl) {
      updateMeta('og:url', canonicalUrl, true);
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);

    const scriptIds: string[] = [];
    if (schemaData && schemaData.length > 0) {
      schemaData.forEach((data, index) => {
        const scriptId = `schema-ld-${Date.now()}-${index}`;
        scriptIds.push(scriptId);
        let script = document.getElementById(scriptId) as HTMLScriptElement | null;
        if (!script) {
          script = document.createElement('script');
          script.type = 'application/ld+json';
          script.id = scriptId;
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(data);
      });
    }

    return () => {
      document.title = currentTitle;
      scriptIds.forEach((id) => {
        const script = document.getElementById(id);
        if (script) script.remove();
      });
    };
  }, [title, description, keywords, canonicalUrl, ogType, schemaData]);

  return null;
}